import { Button, useTranslation } from '@hbs-us/ui';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { DispatchCategoryDto, DispatchEmployeeDto } from './contractsMirror.js';

/**
 * The MANDATORY non-drag scheduling fallback (A11Y-01 / Pitfall 6). Every job card carries a
 * "Schedule…" button that opens this focus-trapped dialog; picking a day + employee + time and
 * confirming hits the SAME `useAssignMutation` POST the drag uses — so the board is FULLY operable
 * without a pointer. Modeled on the `_money/ConfirmDialog` focus-trap (focus-trapped, Esc cancels,
 * returns focus to the trigger on close, `aria-labelledby` its heading). Token-only — no hex.
 *
 * The dialog owns no business logic: it collects the inputs and calls `onConfirm` with the picked
 * values. The parent wires `onConfirm` to the assign mutation (which carries no tenantId — the
 * server resolves the tenant and ownership-checks the employee/category, SEC-11).
 */

const INPUT =
  'mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

// shortcut: a fixed default start until the company dispatch settings exist (Tier 2
// `dispatchDefaultStart`); move it there when they do.
const DEFAULT_START_TIME = '08:00';

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

export interface SchedulePick {
  scheduledDate: string;
  startTime: string;
  endTime: string | null;
  employeeId: number;
  categoryId: number | null;
  customerName: string | null;
  customerPhone: string | null;
  customerAddress: string | null;
}

export function ScheduleFallbackDialog({
  open,
  jobLabel,
  days,
  employees,
  categories,
  defaultDate,
  defaultStartTime,
  error,
  pending,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  jobLabel: string;
  days: string[];
  employees: DispatchEmployeeDto[];
  categories: DispatchCategoryDto[];
  defaultDate?: string;
  /** The time slot a job was dropped on, so the dialog opens where the dispatcher aimed. */
  defaultStartTime?: string;
  /** Why the last save failed, shown in the dialog so the dispatcher can fix it and retry. */
  error?: string | null;
  pending?: boolean;
  onConfirm: (pick: SchedulePick) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const [scheduledDate, setScheduledDate] = useState(defaultDate ?? days[0] ?? '');
  // Never preselect a crew member: one Enter press used to book whoever sorts first by name.
  const [employeeId, setEmployeeId] = useState<number | ''>('');
  const [startTime, setStartTime] = useState(defaultStartTime ?? DEFAULT_START_TIME);
  const [endTime, setEndTime] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');

  // Reset the picker each time the dialog opens for a new card.
  useEffect(() => {
    if (!open) return;
    setScheduledDate(defaultDate ?? days[0] ?? '');
    setEmployeeId('');
    setStartTime(defaultStartTime ?? DEFAULT_START_TIME);
    setEndTime('');
    setCategoryId('');
    setCustomerName('');
    setCustomerPhone('');
    setCustomerAddress('');
  }, [open, defaultDate, defaultStartTime, days]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onCancel],
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = dialogRef.current;
    const firstFocusable = node?.querySelector<HTMLElement>(FOCUSABLE);
    firstFocusable?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const canConfirm = scheduledDate !== '' && employeeId !== '' && startTime !== '';

  const submit = () => {
    if (scheduledDate === '' || startTime === '' || employeeId === '') return;
    onConfirm({
      scheduledDate,
      startTime,
      endTime: endTime || null,
      employeeId,
      categoryId: categoryId === '' ? null : categoryId,
      customerName: customerName.trim() || null,
      customerPhone: customerPhone.trim() || null,
      customerAddress: customerAddress.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={handleKeyDown}
        className="w-full max-w-md rounded-md border border-border bg-card p-6 shadow-lg"
      >
        <h2 id={headingId} className="font-display font-semibold text-foreground text-xl">
          {t('dispatch:fallback.heading', { job: jobLabel })}
        </h2>
        <p className="mt-1 text-muted-foreground text-sm">{t('dispatch:fallback.body')}</p>
        {error ? (
          <p
            role="alert"
            className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-foreground text-sm"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-4 grid gap-4">
          <label className="block text-foreground text-sm" htmlFor={`${headingId}-day`}>
            {t('dispatch:fallback.day')}
            <select
              id={`${headingId}-day`}
              className={INPUT}
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            >
              {days.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-foreground text-sm" htmlFor={`${headingId}-emp`}>
            {t('dispatch:fallback.employee')}
            <select
              id={`${headingId}-emp`}
              className={INPUT}
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">{t('dispatch:fallback.selectEmployee')}</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-start`}>
              {t('dispatch:fallback.startTime')}
              <input
                id={`${headingId}-start`}
                type="time"
                className={INPUT}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </label>
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-end`}>
              {t('dispatch:fallback.endTime')}
              <input
                id={`${headingId}-end`}
                type="time"
                className={INPUT}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </label>
          </div>

          {categories.length > 0 && (
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-cat`}>
              {t('dispatch:fallback.category')}
              <select
                id={`${headingId}-cat`}
                className={INPUT}
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">{t('dispatch:fallback.noCategory')}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block text-foreground text-sm" htmlFor={`${headingId}-cust-name`}>
            {t('dispatch:fallback.customerName')}
            <input
              id={`${headingId}-cust-name`}
              type="text"
              className={INPUT}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-cust-phone`}>
              {t('dispatch:fallback.customerPhone')}
              <input
                id={`${headingId}-cust-phone`}
                type="tel"
                className={INPUT}
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </label>
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-cust-addr`}>
              {t('dispatch:fallback.customerAddress')}
              <input
                id={`${headingId}-cust-addr`}
                type="text"
                className={INPUT}
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {t('dispatch:fallback.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canConfirm || pending}>
            {t('dispatch:fallback.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
